import { useState, type ReactNode } from 'react';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { Switch } from './Switch';

/**
 * Base-map chooser and overlay toggles for a map surface.
 *
 * Lifted out of `MapEditorPage` when printing moved out of the editor: the print
 * preview needs the same picker, and the alternative was a second copy that would
 * drift from this one every time the layer model changed.
 */
export type BaseMapInfo  = { id: string; label: string; url: string; generation: number; widths: number[]; minZ: number; maxZ: number; thumbnailUrl: string | null };
export type OverlayInfo  = { id: string; label: string; url: string; generation: number; widths: number[]; minZ: number; maxZ: number; thumbnailUrl: string | null; iconUrl: string | null; restricted: { account?: string } | null };

/**
 * The generic overlay glyph: what a row falls back to when nothing better is
 * available for that layer.
 */
function GenericLayerIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.onSurfaceVariant} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}

/**
 * Layer glyphs this panel draws itself, keyed by catalogue layer id.
 *
 * The catalogue advertises an icon URL per overlay, but the one it gives for
 * property borders (`/icons/property-borders.svg`) answers 403 from the tile
 * host's asset bucket while its sibling icons answer 200 — so an `<img>` aimed
 * at it renders the browser's broken-image glyph. Mobile never shows that
 * because its remote-SVG widget draws an empty box when the fetch fails; a
 * silent gap is not what this panel wants either.
 *
 * Owning the glyph for a known layer is a deliberate override of what the
 * catalogue serves, the same way an id-specific fix would be applied anywhere
 * else in this panel: the ids this panel recognizes are few and stable, and for
 * those the catalogue's own icon is a fallback rather than the source of
 * truth. An unrecognized layer still takes its icon from the catalogue.
 */
const LOCAL_LAYER_ICONS: Record<string, () => ReactNode> = {
  'world-property': () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.onSurfaceVariant} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3.5" y="5" width="17" height="14" rx="1.5" />
      <path d="M10 5v6.5M10 11.5h10.5" />
    </svg>
  ),
};

function LayerIcon({ layerId, iconUrl }: { layerId: string; iconUrl: string | null }) {
  const [remoteBroken, setRemoteBroken] = useState(false);
  const local = LOCAL_LAYER_ICONS[layerId];

  return (
    <div style={{ width: 22, height: 22, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {local ? (
        local()
      ) : iconUrl && !remoteBroken ? (
        <img src={iconUrl} width={20} height={20} alt="" onError={() => setRemoteBroken(true)} style={{ objectFit: "contain" }} />
      ) : (
        <GenericLayerIcon />
      )}
    </div>
  );
}

export function MapLayersPicker({
  baseMaps,
  overlayMaps,
  selectedMapId,
  enabledOverlays,
  onSelectMap,
  onToggleOverlay,
  onClose,
  side = 'right',
  topOffset = 0,
}: {
  baseMaps: BaseMapInfo[];
  overlayMaps: OverlayInfo[];
  selectedMapId: string | null;
  enabledOverlays: ReadonlySet<string>;
  onSelectMap: (m: BaseMapInfo) => void;
  onToggleOverlay: (id: string) => void;
  onClose: () => void;
/**
 * Which edge the panel anchors to. Defaults to the right, where the map control
 * stack is. The print surface anchors these left, because its print panel holds
 * the right slot permanently and would otherwise be buried by them.
 */
  side?: 'left' | 'right';
  /**
   * Pixels to drop the panel by, so it stacks beneath whatever the surface has
   * already put in this corner — today the hidden-pin-types chip.
   */
  topOffset?: number;
}) {
  const { t } = useTranslation('map');

  return (
    <>
      {/* backdrop — click outside to close */}
      <div data-map-print="hide" style={{ position: "absolute", inset: 0, zIndex: 9 }} onClick={onClose} />

      <div data-map-print="hide" style={{
        position: "absolute", top: 16 + topOffset, ...(side === 'left' ? { left: 16 } : { right: 60 }), zIndex: 10,
        width: 280, maxHeight: `calc(100% - ${32 + topOffset}px)`,
        background: COLORS.surfaceContainerLowest,
        borderRadius: 12, overflow: "hidden",
        boxShadow: "0 4px 24px rgba(0,0,0,0.28)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${COLORS.surfaceContainerHighest}` }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.onSurface }}>{t('layers.title')}</div>
          <div style={{ fontSize: 12, color: COLORS.onSurfaceVariant, marginTop: 1 }}>{t('layers.subtitle')}</div>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {/* Map type grid */}
          {baseMaps.length > 0 && (
            <div style={{ padding: "12px 16px 8px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.onSurface, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>{t('layers.type')}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                {baseMaps.map((m) => {
                  const active = m.id === selectedMapId;
                  return (
                    <button
                      key={m.id}
                      onClick={() => onSelectMap(m)}
                      style={{
                        background: "none", border: "none", padding: 0, cursor: "pointer",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
                      }}
                    >
                      <div style={{
                        width: 52, height: 52, borderRadius: 8, overflow: "hidden",
                        border: active ? `2.5px solid ${COLORS.primary}` : `1.5px solid ${COLORS.surfaceContainerHighest}`,
                        flexShrink: 0,
                      }}>
                        {m.thumbnailUrl ? (
                          <img src={m.thumbnailUrl} width={52} height={52} alt={m.label} style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", background: COLORS.surfaceContainerHigh }} />
                        )}
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: active ? 700 : 500,
                        color: active ? COLORS.primary : COLORS.onSurface,
                        textAlign: "center", lineHeight: 1.2,
                      }}>{m.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Overlay layers */}
          {overlayMaps.length > 0 && (
            <div style={{ padding: "8px 0 8px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.onSurface, margin: "4px 16px 8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>{t('layers.layers')}</div>
              {overlayMaps.map((layer) => {
                const on = enabledOverlays.has(layer.id);
                return (
                  <div
                    key={layer.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 16px",
                      borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
                    }}
                  >
                    {/* icon placeholder */}
                    <LayerIcon layerId={layer.id} iconUrl={layer.iconUrl} />
                    <span style={{ flex: 1, fontSize: 13, color: COLORS.onSurface, fontWeight: 500 }}>{layer.label}</span>
                    <Switch on={on} onChange={() => onToggleOverlay(layer.id)} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state — no overlays (unauthenticated) */}
          {overlayMaps.length === 0 && baseMaps.length > 0 && (
            <div style={{ padding: "12px 16px 16px", color: COLORS.onSurfaceVariant, fontSize: 12 }}>
              {t('layers.signedInHint')}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
